import React, { createContext, useContext, useEffect, useState } from 'react';
import { useAuth0, User } from '@auth0/auth0-react';

import { ACCESS_TOKEN } from '../constants/storage';

interface AuthProviderProps {
  children: React.ReactNode;
}

interface AuthContextProps {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: User | undefined;
  handleLogin: () => Promise<void>;
  handleLogout: () => Promise<void>;
  setTokenOnSession: () => Promise<void>;
}

const AuthContext = createContext({} as AuthContextProps);

const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isTokenStored, setIsTokenStored] = useState(false);
  const {
    isAuthenticated,
    isLoading,
    loginWithRedirect,
    logout,
    user,
    getAccessTokenSilently
  } = useAuth0();

  const setTokenOnSession = async () => {
    try {
      const token = await getAccessTokenSilently();
      sessionStorage.setItem(ACCESS_TOKEN, token);
      setIsTokenStored(true);
    } catch (err) {
      // Sentry.captureException(err, scope => {
      //   scope.setTransactionName(
      //     'Failed to set token on session. Path: src/contexts/AuthContext',
      //   );
      //   scope.setExtras({
      //     screen: 'AuthProvider',
      //     fn: 'setTokenOnSession',
      //   });
      //   return scope;
      // });
    }
  };

  const handleLogin = async () => {
    try {
      await loginWithRedirect({
        appState: {
          returnTo: window.location.pathname
        }
      });
    } catch (err) {
      // Sentry.captureException(err, scope => {
      //   scope.setTransactionName(
      //     'Failed to handle login. Path: src/contexts/AuthContext',
      //   );
      //   scope.setExtras({
      //     screen: 'AuthProvider',
      //     fn: 'handleLogin',
      //   });
      //   return scope;
      // });
      // throw err;
    }
  };

  const handleLogout = async () => {
    try {
      sessionStorage.removeItem(ACCESS_TOKEN);
      await logout();
    } catch (err) {
      // Sentry.captureException(err, scope => {
      //   scope.setTransactionName(
      //     'Failed to handle logout. Path: src/contexts/AuthContext',
      //   );
      //   scope.setExtras({
      //     screen: 'AuthProvider',
      //     fn: 'handleLogout',
      //   });
      //   return scope;
      // });
      // throw err;
    }
  };

  useEffect(() => {
    if (user && isAuthenticated) {
      setTokenOnSession();
    }
  }, [user, isAuthenticated]);

  useEffect(() => {
    async function handleLoginWithRedirect() {
      await loginWithRedirect({
        action: 'signup',
        appState: {
          returnTo: window.location.pathname
        }
      });
    }

    if (!user && !isLoading && !isAuthenticated) handleLoginWithRedirect();
  }, [user, isAuthenticated]);

  return (
    <AuthContext.Provider
      value={{
        user: isTokenStored ? user : undefined, // Only return user if token is stored on session
        isAuthenticated,
        isLoading,
        handleLogin,
        handleLogout,
        setTokenOnSession
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

const useAuthContext = () => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }

  return context;
};

export { AuthProvider, useAuthContext };
